import { Activity, Check, Mic, Repeat, Ruler, Target, ThumbsUp, TriangleAlert } from "lucide-react";

/**
 * The mannequin films (9:16, about 25 s, with the app's real coach voice), in ONE place. Every spot on the site that shows one reads this file:
 *   the home hero (components/home/HeroPhone.jsx, HERO_FILMS below), the sport pages (data/sportStage.js -> SportHero), the Gym and badminton
 *   "Two ways to get coached" cards (SportUploadCTA), the /practice/<slug> guides and the /practice hero.
 *
 * ADD A FILM = drop its mp4 + jpg poster in public/videos (formanti-practice-mannequin-<name>-v1.*; re-render notes: tools/art/mannequin/README.md)
 * and add one entry here, keyed by the sport page key ("badminton", "gym", "tennis", "table_tennis", "pickleball", ...). Nothing else changes: the
 * sport page, its card and its guide pages pick it up, and "Tap for sound" appears (sound: true). A sport with no entry keeps its current stage.
 *
 *   src, poster   the film and its still (the poster is also what reduced-motion, data-saver and low-end phones see)
 *   alt           what the film shows, in words: say only what it shows
 *   label         "Illustrated example". The film draws this label itself (top right of the frame), so the pages do not add a second one
 *   chips         [icon, title, sub] cards that float off the phone: quote only what THIS film shows (a verdict the engine gave, a line the coach says)
 *   caption       one line under the film where a page has room for it (hub guides)
 *   slugs         optional: the /practice/<slug> pages that may show it (all of the sport's pages if left out)
 *   sport, main   optional: a second film for a sport page key (the squat is a "gym" guide film); `main: false` keeps it off the sport page itself
 *   sound         the film carries the coach's voice: RealScreen shows "Tap for sound"
 *
 * The CC BY credits (the motion dataset, the music) are in the site footer, not on the pages.
 */
export const MANNEQUIN_FILMS = {
  // a set of eight squats, the second one stops short of standing tall. Shown on the home hero and /practice, and on the squat guide
  squat: {
    sport: "gym", main: false,
    src: "/videos/formanti-practice-mannequin-squat-v1.mp4", poster: "/videos/formanti-practice-mannequin-squat-v1.jpg",
    alt: "Illustrated example, 26 seconds: a 3D mannequin does a set of eight squats with the app's tracking lines on him. The coach counts every rep aloud, corrects the second rep (he stops short of standing tall: Finish tall), praises the next one (Great lockout), counts down to the last rep and calls the set",
    label: "Illustrated example", sound: true,
    caption: "Illustrated example: a 3D mannequin moved by real gym motion. The checks and the coach's voice are the app's own.",
    slugs: ["squat-form-check"],
    chips: [
      [Activity, "Every rep counted", "Out loud, as you go"],
      [Target, "One rep corrected", "“Finish tall.”"],
      [ThumbsUp, "Praised when fixed", "“Great lockout.”"],
    ],
  },
  // five smashes, the first one with a low reach
  badminton: {
    src: "/videos/formanti-practice-mannequin-badminton-v1.mp4", poster: "/videos/formanti-practice-mannequin-badminton-v1.jpg",
    alt: "Illustrated example, 25 seconds: a 3D mannequin plays five badminton smashes with the app's tracking lines on him. The coach asks for a bend in the knees, flags the first smash for a low reach (Get up higher, meet it above your head), then praises the fix (That's it, that's what we wanted) and the next three, and calls it a really good set",
    label: "Illustrated example", sound: true,
    caption: "Illustrated example: a 3D mannequin with staged smashes. The checks and the coach's voice are the app's own.",
    chips: [
      [Target, "Smash checked live", "Reach, elbow, speed, body"],
      [TriangleAlert, "Low reach flagged", "“Get up higher.”"],
      [ThumbsUp, "Praised when fixed", "“That's it, that's what we wanted.”"],
    ],
  },
  // racquet films (tools/art/mannequin/racquet/FILMS.json): five swings, the first one flagged, then the fix confirmed
  tennis: {
    src: "/videos/formanti-practice-mannequin-tennis-v1.mp4", poster: "/videos/formanti-practice-mannequin-tennis-v1.jpg",
    alt: "Illustrated example: a faceless 3D mannequin practises tennis forehands while the app's live coach flags a late contact beside the body, says how to fix it in its recorded voice, and confirms the next four swings in range.",
    label: "Illustrated example", sound: true,
    caption: "Illustrated example: a 3D mannequin with staged swings. The checks and the coach's voice are the app's own.",
    chips: [
      [TriangleAlert, "Meet it further out in front", "Swing 1 flagged: contact beside the body"],
      [Mic, "“Reach out and hit it in front of you.”", "The coach's recorded voice"],
      [Check, "In range: arm and body", "Swing 2, after the fix"],
    ],
  },
  table_tennis: {
    src: "/videos/formanti-practice-mannequin-table-tennis-v1.mp4", poster: "/videos/formanti-practice-mannequin-table-tennis-v1.jpg",
    alt: "Illustrated example: a faceless 3D mannequin practises table tennis forehand drives while the app's live coach flags a backswing that is too big, says how to fix it in its recorded voice, and confirms the next four swings in range.",
    label: "Illustrated example", sound: true,
    caption: "Illustrated example: a 3D mannequin with staged swings. The checks and the coach's voice are the app's own.",
    chips: [
      [TriangleAlert, "Keep the backswing short", "Swing 1 flagged: too big a wind-up"],
      [Mic, "“Keep it compact. Less backswing.”", "The coach's recorded voice"],
      [Check, "In range: arm and body", "Swing 2, after the fix"],
    ],
  },
  pickleball: {
    src: "/videos/formanti-practice-mannequin-pickleball-v1.mp4", poster: "/videos/formanti-practice-mannequin-pickleball-v1.jpg",
    alt: "Illustrated example: a faceless 3D mannequin practises pickleball dinks while the app's live coach flags straight legs at contact, says how to fix it in its recorded voice, and confirms the next four swings in range.",
    label: "Illustrated example", sound: true,
    caption: "Illustrated example: a 3D mannequin with staged swings. The checks and the coach's voice are the app's own.",
    chips: [
      [TriangleAlert, "Bend your hitting-side knee more at contact", "Swing 1 flagged: legs straight"],
      [Mic, "“Get lower. Sink into it.”", "The coach's recorded voice"],
      [Check, "In range: arm and body", "Swing 2, after the fix"],
    ],
  },
  // lifts and skills (tools/art/mannequin/lifts/FILMS.json)
  gym: {
    src: "/videos/formanti-practice-mannequin-gym-pushup-v1.mp4", poster: "/videos/formanti-practice-mannequin-gym-pushup-v1.jpg",
    alt: "Illustrated example: a faceless 3D mannequin does eight push-ups, moved by joint angles traced from a real gym clip, while the app counts every rep aloud, flags rep 2 for not going deep enough and praises the depth of rep 3.",
    label: "Illustrated example", sound: true,
    caption: "Illustrated example: a 3D mannequin moved by real gym motion. The checks and the coach's voice are the app's own.",
    slugs: ["gym", "push-up-form-check"], // the Gym page and its guides: the push-up film
    chips: [
      [Activity, "Every rep counted aloud", "Eight push-ups, out loud"],
      [Target, "Rep 2: “Lower all the way”", "Elbows did not reach about 90°"],
      [ThumbsUp, "Rep 3: “Great depth”", "The next rep goes deeper"],
    ],
  },
  weight_lifting: {
    src: "/videos/formanti-practice-mannequin-weight-lifting-press-v1.mp4", poster: "/videos/formanti-practice-mannequin-weight-lifting-press-v1.jpg",
    alt: "Illustrated example: a faceless 3D mannequin presses a 40 kg barbell overhead eight times, moved by joint angles traced from a real gym clip (one rep shortened by hand to show a correction), while the app counts each rep and says \"Lock it out\".",
    label: "Illustrated example", sound: true,
    caption: "Illustrated example: a 3D mannequin moved by real gym motion, with one rep shortened by hand to show a correction. The checks and the coach's voice are the app's own.",
    chips: [
      [Activity, "Eight presses, counted aloud", "Rack and lockout checked"],
      [Target, "Rep 2: “Lock it out”", "Arms stopped short of straight"],
      [ThumbsUp, "Rep 3: “Great lockout”", "Arms locked out overhead"],
    ],
  },
  cricket: {
    src: "/videos/formanti-practice-mannequin-cricket-v1.mp4", poster: "/videos/formanti-practice-mannequin-cricket-v1.jpg",
    alt: "Illustrated example: a faceless 3D mannequin shadow-bats five forward defences while the app's live coach counts each shot, flags a stiff front leg on shot 2 and praises the head position on shot 3.",
    label: "Illustrated example", sound: true,
    caption: "Illustrated example: a 3D mannequin with staged shots. The checks and the coach's voice are the app's own.",
    chips: [
      [Activity, "Five forward defences", "Each shot counted and checked"],
      [Target, "Shot 2: “Bend the front knee”", "The front leg stayed stiff"],
      [ThumbsUp, "Shot 3: “Head right over the ball”", "Front knee bent, head over it"],
    ],
  },
  basketball: {
    src: "/videos/formanti-practice-mannequin-basketball-v1.mp4", poster: "/videos/formanti-practice-mannequin-basketball-v1.jpg",
    alt: "Illustrated example: a faceless 3D mannequin shoots five free throws while the app's live coach counts each shot, flags a dropped follow-through on shot 2 and praises the held follow-through on shot 3.",
    label: "Illustrated example", sound: true,
    caption: "Illustrated example: a 3D mannequin with staged shots. The checks and the coach's voice are the app's own.",
    chips: [
      [Activity, "Five free throws", "Set point, release, follow-through"],
      [Target, "Shot 2: “Hold the follow-through”", "The arm dropped once the ball left"],
      [ThumbsUp, "Shot 3: “Great follow-through”", "Arm held up after the release"],
    ],
  },
  // the ANALYSE film (tools/art/mannequin/analysis/FILMS.json): a rally is uploaded, four smashes are found, the pose is redrawn with the arm in green, a fix and a drill. NO voice (a quiet music bed only): `sound: false`, so it is a muted loop with no "Tap for sound".
  // Not a sport page: read it with analysisFilm() (components on the home page, the sport pages' "Analyse" option and the /analyze header).
  analysis: {
    src: "/videos/formanti-analysis-mannequin-v1.mp4", poster: "/videos/formanti-analysis-mannequin-v1.jpg",
    alt: "Illustrated example: a faceless 3D mannequin's badminton rally is uploaded, the app finds four smashes, redraws his pose on one of them with the arm in green where it should be, and gives a sample shot score with the fix and a drill.",
    label: "Illustrated example", sound: false,
    chips: [
      [Target, "4 smashes found and marked", "Upload one rally"],
      [Ruler, "Lift your hitting arm a lot higher at contact", "Arm height 105° → 170°, redrawn in green"],
      [Repeat, "Shadow 10 swings in slow motion and freeze at contact", "The drill that comes with the fix"],
    ],
  },
};

/** Where "Tap for sound" sits on a 9:16 film: in the empty band above the mannequin, below the coach's banner, clear of the report cards at the bottom. */
export const FILM_PILL = "top-[23%] bottom-auto";

/** The two films the home hero shows together: [front phone, back phone]. */
export const HERO_FILMS = ["squat", "badminton"];

/**
 * The mannequin film for a sport key (and, on a /practice/<slug> guide, the slug) as a RealScreen `video` with its `chips`, or null.
 * `strength` (the practice code's key) and `gym` are the same page. An entry belongs to the sport page it is keyed by, or to `sport` if it says so;
 * `slugs` limits it to those guides (and `main: false` keeps it off the sport page itself); with no `slugs` it serves all of the sport's pages.
 */
export function mannequinFilmFor(sport, slug) {
  const page = sport === "strength" ? "gym" : sport;
  for (const [key, f] of Object.entries(MANNEQUIN_FILMS)) {
    if ((f.sport || key) !== page) continue;
    if (slug ? f.slugs && !f.slugs.includes(slug) : f.main === false) continue;
    return { src: f.src, poster: f.poster, alt: f.alt, sound: f.sound !== false, film: true, label: f.label, caption: f.caption, chips: f.chips };
  }
  return null;
}

/** The Analyse film (a muted illustrated loop, no voice) as a RealScreen `video` with its `chips`. */
export const analysisFilm = () => mannequinFilmFor("analysis");
